import { createMeter, type Meter } from './meter.js';
import { webRtcTransport } from './webrtc.js';
import { webSocketTransport, webSocketServiceUrl } from './websocket.js';
import {
  RuntimeMismatchError,
  type Talk,
  type TalkListeners,
  type TalkOptions,
  type TalkStatus,
  type Transport,
  type TransportHooks,
} from './types.js';

/**
 * Start a live conversation with a LineAI agent.
 *
 * Call it from a user gesture (a tap or click): browsers only allow the
 * microphone and audio playback that way. It returns immediately and starts
 * connecting on the next microtask, so listeners attached right after the
 * call still receive the first `connecting` status.
 *
 * Runtime selection is server-authoritative: the WebRTC runtime is asked
 * first and answers 409 for agents on the Grok runtime, which then takes over.
 */
export const startTalk = (options: TalkOptions): Talk => {
  if (!options.agent) throw new Error('startTalk: `agent` (the agent’s E.164 number) is required');

  const listeners: { [K in keyof TalkListeners]: Set<TalkListeners[K]> } = {
    status: new Set(),
    event: new Set(),
    transcript: new Set(),
    error: new Set(),
    mute: new Set(),
  };
  const emit = <K extends keyof TalkListeners>(name: K, ...args: Parameters<TalkListeners[K]>) => {
    for (const fn of listeners[name]) {
      try { (fn as (...a: unknown[]) => void)(...args); } catch (e) { console.error(`[talk] ${name} listener threw`, e); }
    }
  };

  // Created synchronously inside the user's gesture so playback is unlocked
  // (iOS in particular). Agent audio and both level meters run on it.
  // iOS: declare a two-way voice session so the silent switch doesn't mute
  // Web Audio playback (Safari 16.4+; ignored elsewhere).
  const session = (navigator as Navigator & { audioSession?: { type: string } }).audioSession;
  if (session) { try { session.type = 'play-and-record'; } catch { /* unsupported value */ } }
  const ctx = new AudioContext();
  void ctx.resume();
  const agentMeter = createMeter(ctx);
  const agentOut = ctx.createGain();
  agentOut.connect(ctx.destination);
  agentOut.connect(agentMeter.input);
  let callerMeter: Meter | null = null;

  let status: TalkStatus = 'connecting';
  let muted = false;
  let ended = false;
  let active: Transport | null = null;

  const setStatus = (next: TalkStatus) => {
    if (next === status && next !== 'connecting') return;
    status = next;
    emit('status', next);
    if (next === 'ended' || next === 'error') {
      ended = true;
      // Let the last syllable finish before releasing audio.
      setTimeout(() => { void ctx.close().catch(() => {}); }, 400);
    }
  };

  const hooks: TransportHooks = {
    status: (s) => {
      if (s === 'connecting') { if (status !== 'connecting') setStatus('connecting'); return; }
      setStatus(s === 'disconnected' ? 'ended' : s);
    },
    error: (message) => emit('error', message),
    event: (event) => emit('event', event),
    transcript: (transcript) => emit('transcript', transcript),
    callerStream: (stream) => {
      if (ctx.state === 'closed') return;
      const meter = createMeter(ctx);
      ctx.createMediaStreamSource(stream).connect(meter.input);
      callerMeter = meter;
    },
    agentStream: (stream) => {
      if (ctx.state === 'closed') return;
      // Metered only: the transport's <audio> element does the playing.
      ctx.createMediaStreamSource(stream).connect(agentMeter.input);
    },
    ctx,
    agentOut,
    log: options.debug ? (...args) => console.log('[talk]', ...args) : () => {},
  };

  const run = async () => {
    emit('status', 'connecting');
    let token: string | null = null;
    try {
      token = options.tokenProvider ? await options.tokenProvider() : options.token ?? null;
    } catch (e) {
      console.warn('[talk] tokenProvider threw; continuing anonymously', e);
    }
    if (ended) return;

    const webrtc = webRtcTransport(hooks);
    active = webrtc;
    try {
      await webrtc.call(options.agent, token);
    } catch (err) {
      if (!(err instanceof RuntimeMismatchError) || ended) return;
      // service-ws names the runtime in its 409: grok -> service-grok, live -> service-live.
      const web = webSocketTransport(hooks, webSocketServiceUrl(err.runtime));
      active = web;
      try {
        await web.call(options.agent, token);
      } catch {
        hooks.error('This agent is not reachable on either voice runtime');
        setStatus('error');
      }
    }
    if (muted) active?.setMuted(true);
  };
  queueMicrotask(() => { void run(); });

  return {
    get status() { return status; },
    get muted() { return muted; },
    levels() {
      if (ctx.state === 'closed') return { agent: 0, caller: 0 };
      return { agent: agentMeter.read(), caller: muted || !callerMeter ? 0 : callerMeter.read() };
    },
    mute(next = !muted) {
      if (next === muted) return muted;
      muted = next;
      active?.setMuted(muted);
      emit('mute', muted);
      return muted;
    },
    end() {
      if (ended) return;
      ended = true;
      if (active) active.hangUp();
      // Hung up before a transport existed, or the transport was already idle.
      if (status !== 'ended' && status !== 'error') setStatus('ended');
    },
    on(name, listener) {
      (listeners[name] as Set<typeof listener>).add(listener);
      return () => { (listeners[name] as Set<typeof listener>).delete(listener); };
    },
  };
};
